import { useTranslation } from "react-i18next";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Text } from "@/components/ui/Text";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectSeparator,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import type { CharacterChoiceProps } from "@/types/creative-library";

const NEW_MODEL_VALUE = "__new_model__";

export function CharacterChoice({
  brand,
  value,
  disabled,
  list,
  creatingNew,
  onChange,
  onCreate,
  onOpen,
}: CharacterChoiceProps) {
  const { t } = useTranslation("app");
  const { subjects, loading, error } = list;
  const canCreate = Boolean(brand?.canEdit);
  const hasOpen =
    onOpen &&
    value !== "none" &&
    subjects.some((subject) => subject.token === value);
  return (
    <div className="flex min-w-0 flex-col gap-2">
      <Label>{t("creativeLibrary.subject")}</Label>
      <Select
        value={creatingNew ? NEW_MODEL_VALUE : value}
        disabled={disabled || !brand || loading || error}
        onValueChange={(token) => {
          if (token === NEW_MODEL_VALUE) {
            onCreate();
            return;
          }
          onChange(
            token,
            subjects.find((subject) => subject.token === token),
          );
        }}
      >
        <SelectTrigger
          aria-label={t("creativeLibrary.subject")}
          data-testid="character-choice"
        >
          <SelectValue placeholder={t("creativeLibrary.chooseCharacter")} />
        </SelectTrigger>
        <SelectContent>
          <SelectItem value="none">
            {t("creativeLibrary.chooseCharacter")}
          </SelectItem>
          {subjects.map((subject) => (
            <SelectItem
              key={subject.token}
              value={subject.token}
              disabled={!subject.canEdit}
            >
              {subject.name}
            </SelectItem>
          ))}
          {value !== "none" &&
            !subjects.some((item) => item.token === value) && (
              <SelectItem value={value} disabled>
                {t("creativeLibrary.unavailableSelection")}
              </SelectItem>
            )}
          {canCreate && (
            <>
              <SelectSeparator />
              <SelectItem
                value={NEW_MODEL_VALUE}
                data-testid="character-choice-new"
              >
                {t("creativeLibrary.newSubject")}
              </SelectItem>
            </>
          )}
        </SelectContent>
      </Select>
      {(hasOpen || error) && (
        <div className="flex flex-wrap items-center gap-2">
          {hasOpen && (
            <Button
              type="button"
              variant="outline"
              size="sm"
              disabled={disabled}
              onClick={() =>
                onOpen(subjects.find((subject) => subject.token === value)!)
              }
              data-testid="character-choice-open"
            >
              {t("creativeLibrary.manageAngles")}
            </Button>
          )}
          {error && (
            <Button
              variant="ghost"
              size="sm"
              disabled={disabled}
              onClick={list.retry}
              data-testid="character-choice-reload"
            >
              {t("creativeLibrary.reload")}
            </Button>
          )}
        </div>
      )}
      {(error || !brand) && (
        <Text size="xs" color="muted" role={error ? "alert" : undefined}>
          {t(
            error
              ? "creativeLibrary.loadFailed"
              : !brand
                ? "creativeLibrary.chooseCharacterBrand"
                : "creativeLibrary.characterUploadHint",
          )}
        </Text>
      )}
    </div>
  );
}
